/**
 * The human title for every drillable metric key — P160 item 5.
 *
 * ⛔⛔ WHY THIS IS ITS OWN FILE. v2.1.12 opened a panel headed
 * `ceo.open_pipeline_value`, because the title fell back to the key itself.
 * The set of drillable metrics is decided by the SERVER registries; their
 * names are decided here. That is one contract in two places — the defect this
 * repo repeats more than any other — so the map lives in a plain module with
 * no JSX, which `tests/unit/security/drilldownTitleCoverage.test.js` joins
 * against the registries. A metric registered without a title now fails a
 * test instead of shipping its own identifier onto the CEO's screen.
 */

export const METRIC_TITLES = Object.freeze({
  'ceo.open_pipeline_value': 'Open pipeline value',
  'ceo.open_opportunities': 'Open opportunities',
  'ceo.booked_won_value': 'Booked (won) value',
  'ceo.new_leads': 'New leads',
});

export const FINANCE_TITLES = Object.freeze({
  'finance.invoiced': 'Invoiced',
  'finance.collected': 'Collected',
  'finance.overdue': 'Overdue',
  'finance.deposits': 'Deposits',
  'finance.refunded': 'Refunds',
  // ⭐ P170. Named distinctly from 'Overdue' ON PURPOSE: they are different
  // sets of invoices that overlap, and two cards reading "Overdue" would invite
  // exactly the addition that double-counts.
  'finance.unpaid': 'Unpaid invoices (due on demand)',
});

export const REP_TITLES = Object.freeze({
  'rep.overdue_tasks': 'Overdue follow-ups',
  'rep.open_pipeline': 'Open pipeline',
  'rep.won': 'Deals won',
  'rep.new_leads': 'Leads assigned',
  'attention.stuck_deals': 'Stuck deals',
  'attention.uncontacted_leads': 'New leads with no contact',
  'attention.consult_no_shows': 'Consult no-shows not rescheduled',
});

const ALL = [METRIC_TITLES, FINANCE_TITLES, REP_TITLES];

/**
 * A human title for a metric key, or null.
 *
 * ⚠ RETURNS NULL for an unknown key — it does not invent one and it does not
 * pass the key through. The caller renders nothing rather than a storage key:
 * a missing panel is a visible bug and gets reported, a key-as-title is
 * invisible and ships.
 *
 * ⛔ `hasOwnProperty`, not a bare lookup. `metricTitle('constructor')` on a
 * plain object read returns a FUNCTION, which is truthy and would render.
 */
export function metricTitle(key) {
  if (typeof key !== 'string' || !key) return null;
  for (const map of ALL) {
    if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  }
  return null;
}
